import React, { useState, useEffect, useRef } from 'react';
import Calendar from './Calendar';
import type { TimetableEntry } from '../types/timetable';

interface FloatingControlsProps {
  isDarkMode: boolean;
  selectedLocation: string;
  selectedTeacher: string;
  showSunday: boolean;
  onLocationChange: (location: string) => void;
  onTeacherChange: (teacher: string) => void;
  onToggleSunday: (show: boolean) => void;
  onToggleDarkMode: () => void;
  timetableData: TimetableEntry[];
}

const FloatingControls: React.FC<FloatingControlsProps> = ({
  isDarkMode,
  selectedLocation,
  selectedTeacher,
  showSunday,
  onLocationChange,
  onTeacherChange,
  onToggleSunday,
  onToggleDarkMode,
  timetableData
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const fabRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (fabRef.current && !fabRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isExpanded]);

  return (
    <div className="floating-controls" ref={fabRef}>
      <div className={`fab-menu ${isExpanded ? 'expanded' : ''}`}>
        <div className="fab-controls">
          <Calendar.LocationSelector 
            isDarkMode={isDarkMode}
            selectedLocation={selectedLocation}
            onLocationChange={onLocationChange}
            timetableData={timetableData}
          />
          <Calendar.TeacherSelector 
            isDarkMode={isDarkMode}
            selectedTeacher={selectedTeacher}
            onTeacherChange={onTeacherChange}
            timetableData={timetableData}
          />
          <button 
            className={`sunday-toggle ${showSunday ? 'active' : ''}`}
            onClick={() => onToggleSunday(!showSunday)}
            aria-label="Toggle Sunday view"
          >
            {showSunday ? '📅' : '📆'} Yakshanba
          </button>
          <button 
            className="theme-toggle"
            onClick={onToggleDarkMode}
            aria-label="Toggle dark mode"
          >
            {isDarkMode ? '☀️' : '🌙'}
          </button>
        </div>
      </div>
      
      <button 
        className={`fab-main ${isExpanded ? 'expanded' : ''}`}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-label="Toggle controls"
      >
        {isExpanded ? '✕' : '⚙️'}
      </button>
    </div>
  );
};

export default FloatingControls;
